// Gravar a georreferenciação num IFC (o "Gravar georref no IFC" do
// editor de posição): o CRS e a conversão (writeGeorefToIfc), o datum
// vertical e, se pedido, a latitude/longitude do IfcSite a partir da
// origem do sítio no mapa. Um modelo de cada vez ou todos os abertos.
// Com `tirarCoordenadasGrandes`, antes disso traz a geometria para junto
// da origem e soma a deslocação aos Eastings/Northings.
import { detectProjectMetresPerUnit, writeGeorefToIfc } from '@viewer/lib/georefWrite'
import type { GeoPlacement } from '@viewer/lib/geoTransform'
import { bytesToLatin1, latin1ToBytes } from '@viewer/lib/stepText'
import { metrosDoCrs, resolverNoPonto } from '@viewer/map/crsDefs'
import { loadProj4 } from '@viewer/map/proj4Lazy'
import { escreverLatLonNoSitio, origemDoSitio, pontoNoMapa } from './sitioLatLon'
import { tirarCoordenadasGrandes } from './tirarCoordenadasGrandes'

export interface GravacaoDaGeoref {
  bytes: Uint8Array
  schema: string
  removidas: number
  /** A latitude/longitude escrita no IfcSite, ou null. */
  sitio: { lat: number; lon: number } | null
  /** Porque não se escreveu a latitude/longitude, quando foi pedida. */
  avisoDoSitio: string | null
  /** A deslocação tirada das posições (metros), ou null. */
  recentragem: { metros: number } | null
  /** Porque não se tiraram as coordenadas grandes, quando foi pedido e
   *  havia. */
  avisoDaRecentragem: string | null
}

/** O datum vertical como o campo o recebe: vazio = manter o do ficheiro;
 *  um número = EPSG. */
export function datumDoCampo(texto: string): string | undefined {
  const v = texto.trim()
  if (!v) return undefined
  return /^\d+$/.test(v) ? `EPSG:${v}` : v.toUpperCase().startsWith('EPSG:') ? `EPSG:${v.slice(5).trim()}` : v
}

export async function gravarGeorefNoIfc(
  bytes: Uint8Array,
  opts: {
    epsg: string
    placement: GeoPlacement
    /** undefined = manter o do ficheiro. */
    verticalDatum?: string
    corrigirSitio: boolean
    /** Trazer a geometria para junto da origem, se a deslocação estiver
     *  toda na colocação do IfcSite. */
    tirarCoordenadasGrandes?: boolean
  },
): Promise<GravacaoDaGeoref> {
  const crsMetresPerUnit = await metrosDoCrs(opts.epsg)
  let placement = opts.placement
  let entrada = bytes
  let recentragem: GravacaoDaGeoref['recentragem'] = null
  let avisoDaRecentragem: string | null = null
  if (opts.tirarCoordenadasGrandes) {
    const texto = bytesToLatin1(bytes)
    const mpu = detectProjectMetresPerUnit(texto)
    const rec = mpu ? tirarCoordenadasGrandes(texto, mpu) : null
    if (rec?.ok) {
      const [eastings, northings] = pontoNoMapa(rec.dx, rec.dy, placement, mpu!)
      placement = { ...placement, eastings, northings }
      entrada = latin1ToBytes(rec.texto)
      recentragem = { metros: Math.hypot(rec.dx, rec.dy) * mpu! }
    } else if (rec && !rec.nada) {
      avisoDaRecentragem = `Coordenadas grandes mantidas: ${rec.motivo}`
    } else if (!mpu) {
      avisoDaRecentragem = 'Coordenadas grandes mantidas: unidade de comprimento do projecto não encontrada.'
    }
  }
  const r = writeGeorefToIfc(entrada, {
    epsg: opts.epsg,
    placement,
    crsMetresPerUnit,
    ...(opts.verticalDatum !== undefined ? { verticalDatum: opts.verticalDatum } : {}),
  })
  if (!opts.corrigirSitio) {
    return { bytes: r.bytes, schema: r.schema, removidas: r.removed, sitio: null, avisoDoSitio: null, recentragem, avisoDaRecentragem }
  }
  let texto = bytesToLatin1(r.bytes)
  const origem = origemDoSitio(texto)
  const semSitio = (aviso: string): GravacaoDaGeoref => ({
    bytes: r.bytes,
    schema: r.schema,
    removidas: r.removed,
    sitio: null,
    avisoDoSitio: aviso,
    recentragem,
    avisoDaRecentragem,
  })
  if (!origem) return semSitio('Não se encontrou a colocação do IfcSite: a latitude/longitude ficou como estava.')
  if (origem.relativa) {
    return semSitio('A colocação do IfcSite é relativa a outra: a latitude/longitude ficou como estava.')
  }
  const [e, n] = pontoNoMapa(origem.x, origem.y, placement, r.projectMetresPerUnit)
  const def = (await resolverNoPonto(opts.epsg, [e, n]))?.def
  if (!def) return semSitio(`Não foi possível converter ${opts.epsg} para latitude/longitude.`)
  const proj4 = await loadProj4()
  const [lon, lat] = proj4(def, 'WGS84', [e, n]) as [number, number]
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
    return semSitio('A origem do IfcSite não dá uma latitude/longitude válida: ficou como estava.')
  }
  texto = escreverLatLonNoSitio(texto, lat, lon).texto
  return {
    bytes: latin1ToBytes(texto),
    schema: r.schema,
    removidas: r.removed,
    sitio: { lat, lon },
    avisoDoSitio: null,
    recentragem,
    avisoDaRecentragem,
  }
}
